import { useCallback, useEffect, useRef } from "react";
import { CATEGORY_PAGE_ID_FACET } from "src/utils/algolia";
import { useCategoryTreeDispatch } from "../context/use-category-tree-context";
import { IndexUiState } from "instantsearch.js";
import {
  useCurrentRefinements,
  useRefinementList,
} from "react-instantsearch-core-v7";

export type RefineCategoryPageIdProps = {
  id: string;
  parentId: string;
  categoryPageId: string;
  parentCategoryId: string | undefined;
  action: "add" | "remove" | "remove-all" | "remove-all-sub";
};

function useRefineCategoryPageId({
  mainCategoryPageId,
  setIndexUiState,
  items = [],
}: {
  items: string[];
  mainCategoryPageId: string;
  isLoadingComplete: boolean;
  setIndexUiState: (
    props: IndexUiState | ((prevState: IndexUiState) => IndexUiState)
  ) => void;
}): {
  refineCategoryPageId: (props: RefineCategoryPageIdProps) => void;
} {
  useRefinementList({ attribute: CATEGORY_PAGE_ID_FACET });

  const treeDispatch = useCategoryTreeDispatch();

  const itemsSet = useRef(false);
  const categoryPageIdRef = useRef<string>();
  const itemsLengthRef = useRef(0);
  const { items: refinementList } = useCurrentRefinements();

  const refineCategoryPageId = useCallback(
    ({
      categoryPageId,
      parentCategoryId,
      action,
    }: RefineCategoryPageIdProps) => {
      const categoryPageIdState = refinementList.flatMap((item) =>
        item.attribute === CATEGORY_PAGE_ID_FACET
          ? item.refinements.flatMap((categories) => [categories.value])
          : []
      );

      const hasParentCategory = parentCategoryId
        ? categoryPageIdState.includes(parentCategoryId) || false
        : false;

      let currentRefinements =
        (categoryPageIdState.filter((catPageId) =>
          action === "remove" ? catPageId !== categoryPageId : true
        ) as string[]) || [];

      const children = currentRefinements.filter((catPageId) =>
        catPageId.startsWith(categoryPageId)
      );

      const siblings = parentCategoryId
        ? currentRefinements.filter(
            (catPageId) =>
              catPageId.startsWith(parentCategoryId) &&
              catPageId !== categoryPageId &&
              !children.includes(catPageId)
          )
        : [];

      if (action === "add" && hasParentCategory) {
        currentRefinements = currentRefinements.filter(
          (catPageId) => catPageId !== parentCategoryId
        );
      }

      if (
        action === "remove" &&
        !siblings?.length &&
        parentCategoryId &&
        !currentRefinements.includes(parentCategoryId)
      ) {
        currentRefinements = [...currentRefinements, parentCategoryId];
      }

      if (action === "remove" && !parentCategoryId && children.length) {
        currentRefinements = currentRefinements.flatMap((catPageId) => {
          if (children.includes(catPageId)) {
            return [];
          }

          if (categoryPageId === catPageId) {
            return [];
          }

          return [catPageId];
        });
      }

      if (action === "remove-all") {
        currentRefinements = currentRefinements.flatMap((catPageId) => {
          if (catPageId.startsWith(categoryPageId)) {
            return [];
          }

          return [catPageId];
        });
      }

      if (action === "remove-all-sub" && parentCategoryId) {
        currentRefinements = currentRefinements.flatMap((catPageId) => {
          if (catPageId.startsWith(categoryPageId)) {
            return [];
          }

          return [catPageId];
        });

        if (siblings.length === 0) {
          currentRefinements.push(parentCategoryId);
        }
      }

      if (
        action === "remove-all-sub" &&
        !parentCategoryId &&
        children.length > 0
      ) {
        currentRefinements = currentRefinements.flatMap((catPageId) => {
          if (catPageId.startsWith(categoryPageId)) {
            return [];
          }

          return [catPageId];
        });
      }

      setIndexUiState((prevState) => ({
        ...prevState,
        page: 0,
        refinementList: {
          ...prevState.refinementList,
          [CATEGORY_PAGE_ID_FACET]:
            action === "add" && children.length === 0
              ? [...currentRefinements, categoryPageId]
              : [...currentRefinements],
        },
      }));
    },
    [refinementList, setIndexUiState]
  );

  useEffect(() => {
    treeDispatch({ type: "SET_REFINE", payload: refineCategoryPageId });
  }, [refineCategoryPageId, treeDispatch]);

  useEffect(() => {
    if (categoryPageIdRef.current !== mainCategoryPageId) {
      categoryPageIdRef.current = mainCategoryPageId;
      itemsSet.current = false;

      treeDispatch({ type: "SET_LOADING", payload: true });

      return;
    }
  }, [mainCategoryPageId, treeDispatch]);

  useEffect(() => {
    if (itemsSet.current || items.length === 0) {
      return;
    }

    treeDispatch({ type: "SET_ITEMS", payload: items });

    itemsSet.current = true;
    itemsLengthRef.current = items.length;
  }, [items, treeDispatch]);

  return {
    refineCategoryPageId,
  };
}

export { useRefineCategoryPageId };
